import { type VariantProps, cva } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

/* Mismo control para input, textarea y select: borde fuerte sobre el fondo de
 * la app, no sobre la tarjeta, así se lee como un hueco para escribir. */
const control = cva(
  "border-border-strong bg-background text-foreground placeholder:text-muted-foreground w-full border " +
    "transition-colors disabled:cursor-not-allowed disabled:opacity-50 " +
    "aria-[invalid=true]:border-danger",
  {
    variants: {
      size: {
        lg: "rounded-row h-12 px-3 text-sm",
        md: "rounded-chip h-11 px-3 text-sm",
        sm: "rounded-chip h-10 px-2.5 text-[13px]",
        xs: "rounded-chip h-9 px-2.5 text-[13px]",
      },
      mono: { true: "font-mono text-[13px]", false: "" },
    },
    defaultVariants: { size: "md", mono: false },
  },
);

type ControlVariants = VariantProps<typeof control>;

export interface InputProps extends Omit<ComponentProps<"input">, "size">, ControlVariants {
  leadingIcon?: LucideIcon;
  invalid?: boolean;
}

export function Input({
  size,
  mono,
  leadingIcon: Icon,
  invalid,
  className,
  ...props
}: InputProps) {
  const input = (
    <input
      aria-invalid={invalid || undefined}
      className={cn(control({ size, mono }), Icon && "pl-9", className)}
      {...props}
    />
  );
  if (!Icon) return input;
  return (
    <div className="relative">
      <Icon
        aria-hidden
        strokeWidth={1.75}
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-[15px] -translate-y-1/2"
      />
      {input}
    </div>
  );
}

export interface TextareaProps extends ComponentProps<"textarea"> {
  tone?: "default" | "code";
  compact?: boolean;
  invalid?: boolean;
}

export function Textarea({
  tone = "default",
  compact,
  invalid,
  className,
  ...props
}: TextareaProps) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={cn(
        control({ size: "md" }),
        "h-auto min-h-24 resize-y px-3.5 py-3 leading-[1.55]",
        compact ? "rounded-chip" : "rounded-tile",
        tone === "code" && "bg-surface-code text-code-foreground font-mono text-xs",
        className,
      )}
      {...props}
    />
  );
}

export interface SelectProps extends Omit<ComponentProps<"select">, "size">, ControlVariants {
  invalid?: boolean;
}

export function Select({ size, mono, invalid, className, ...props }: SelectProps) {
  return (
    <select
      aria-invalid={invalid || undefined}
      className={cn(control({ size, mono }), "cursor-pointer pr-8", className)}
      {...props}
    />
  );
}
